import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { firestore, doc, getDoc } from "../firebase-config";
import Layout from "../layouts/Layout";
import "../styles/SermonDetail.css";

const SermonDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sermon, setSermon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playingAudio, setPlayingAudio] = useState(false);

  useEffect(() => {
    const fetchSermon = async () => {
      try {
        const sermonDoc = await getDoc(doc(firestore, "sermons", id));
        if (sermonDoc.exists()) {
          setSermon({ id: sermonDoc.id, ...sermonDoc.data() });
        }
      } catch (error) {
        console.error("Error fetching sermon:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSermon();
  }, [id]);

  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(
      /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([^&?/\s]{11})/
    );
    return match ? match[1] : null;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <Layout featureType="sermon_detail">
        <div className="sermon-detail">
          <p>Loading sermon...</p>
        </div>
      </Layout>
    );
  }

  if (!sermon) {
    return (
      <Layout featureType="sermon_detail">
        <div className="sermon-detail">
          <p>Sermon not found.</p>
          <button onClick={() => navigate("/online-sermons")} className="back-btn">
            ← Back to Sermons
          </button>
        </div>
      </Layout>
    );
  }

  const youtubeId = getYouTubeId(sermon.youtubeUrl);

  return (
    <Layout featureType="sermon_detail">
      <div className="sermon-detail">
        <button onClick={() => navigate("/online-sermons")} className="back-btn">
          ← Back to Sermons
        </button>

        <div className="sermon-detail-header">
          <h2>{sermon.title}</h2>
          <div className="sermon-detail-meta">
            {sermon.speaker && <span className="meta-speaker">{sermon.speaker}</span>}
            {sermon.date && <span className="meta-date">{formatDate(sermon.date)}</span>}
            {sermon.series && <span className="meta-series">Series: {sermon.series}</span>}
            {sermon.scripture && <span className="meta-scripture">{sermon.scripture}</span>}
          </div>
        </div>

        {/* YouTube embed (primary) */}
        {youtubeId && (
          <div className="sermon-video">
            <iframe
              src={`https://www.youtube.com/embed/${youtubeId}?rel=0`}
              title={sermon.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        )}

        {/* Uploaded video file (fallback or alternative to YouTube) */}
        {sermon.videoFileUrl && (
          <div className="sermon-video-file">
            <h3>{youtubeId ? "Or watch the uploaded video:" : "Watch:"}</h3>
            <video controls src={sermon.videoFileUrl} className="video-player">
              Your browser does not support the video element.
            </video>
          </div>
        )}

        {/* Audio player (fallback or additional) */}
        {sermon.audioFileUrl && (
          <div className="sermon-audio">
            <h3>{youtubeId || sermon.videoFileUrl ? "Or listen to the audio:" : "Listen:"}</h3>
            <audio controls src={sermon.audioFileUrl} className="audio-player">
              Your browser does not support the audio element.
            </audio>
          </div>
        )}

        {/* If no media is available */}
        {!youtubeId && !sermon.videoFileUrl && !sermon.audioFileUrl && (
          <div className="sermon-no-media">
            <p>No media available for this sermon at the moment.</p>
          </div>
        )}

        {/* Description/notes if available */}
        {sermon.description && (
          <div className="sermon-description">
            <h3>Notes</h3>
            <p>{sermon.description}</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SermonDetail;
